import type { FilterOption } from '../../filters/config/types';
import type {
	ColorValue,
	ColumnConfiguration,
	ListBuilderConfiguration,
	ListBuilderList,
} from '../types';

/**
 * PD-16573 — the Form Submissions inbox (docs/claude/forms/01-design.md §7), backed by
 * GET /api/form-submissions (crm-api FormSubmissionsService.list). The tabs are the
 * inbox's work queues, each a locked filter; All has none, so the filter panel narrows it
 * any way. A row opens the submission at /forms/submissions/:id.
 */

const TABLE = 'form_submission';

const STATUS_STYLE: Record<string, ColorValue> = {
	New: { type: 'info' },
	'In review': { type: 'warning' },
	Converted: { type: 'success' },
	Linked: { type: 'success' },
	Dismissed: { type: 'default' },
};

const SPAM_STYLE: Record<string, ColorValue> = {
	Passed: { type: 'default' },
	Spam: { type: 'danger' },
	Duplicate: { type: 'warning' },
	Unverified: { type: 'warning' },
};

function text(
	id: string,
	fieldName: string,
	secondary = false,
): NonNullable<ColumnConfiguration['fields']>[number] {
	return {
		id,
		dataConfig: { type: 'field', field: { fieldName, objectTable: TABLE } },
		styleConfig: secondary
			? {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				}
			: { size: 'md', colorRule: { type: 'default' } },
		displayStyle: 'text',
		// A second line with nothing in it is left out rather than showing a dash.
		...(secondary
			? {
					conditionalVisibility: { field: fieldName, condition: 'is_not_null' },
				}
			: {}),
	};
}

/** A line break, left out with the line it opens when that line's field is empty. */
function lineBreak(
	id: string,
	whenField?: string,
): NonNullable<ColumnConfiguration['fields']>[number] {
	return {
		id,
		dataConfig: { type: 'custom_text', customText: '' },
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		displayStyle: 'line_break',
		...(whenField != null
			? {
					conditionalVisibility: { field: whenField, condition: 'is_not_null' },
				}
			: {}),
	};
}

const COLUMNS: Array<ColumnConfiguration> = [
	{
		id: 'received',
		fields: [
			{
				id: 'received-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'createdDate', objectTable: TABLE },
				},
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
					formatOptions: { dateFormat: 'datetime-short' },
				},
				displayStyle: 'datetime',
			},
		],
		headers: [{ id: 'h1', text: 'Received' }],
		minWidth: 175,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'DESC', fieldName: 'createdDate', objectTable: TABLE },
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'submitter',
		fields: [
			text('submitter-name', 'submitterName'),
			lineBreak('submitter-break-1', 'submitterCompany'),
			text('submitter-company', 'submitterCompany', true),
			lineBreak('submitter-break-2', 'submitterEmail'),
			text('submitter-email', 'submitterEmail', true),
		],
		headers: [{ id: 'h1', text: 'From' }],
		minWidth: 220,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'ASC', fieldName: 'submitterName', objectTable: TABLE },
		],
		widthConfig: { mode: 'flexible', preset: 'large' },
		verticalAlignment: 'top',
	},
	{
		id: 'form',
		fields: [
			text('form-name', 'formName'),
			lineBreak('form-break', 'storefrontName'),
			text('form-storefront', 'storefrontName', true),
		],
		headers: [{ id: 'h1', text: 'Form' }],
		minWidth: 180,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'matched',
		fields: [
			text('matched-summary', 'matchSummary'),
			lineBreak('matched-break', 'clientName'),
			text('matched-client', 'clientName', true),
		],
		headers: [{ id: 'h1', text: 'Who it is' }],
		minWidth: 200,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'status',
		fields: [
			{
				id: 'status-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'statusLabel', objectTable: TABLE },
				},
				displayStyle: 'badge',
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'status_map', statusMap: STATUS_STYLE },
				},
			},
			lineBreak('status-break', 'failedActionsLabel'),
			{
				id: 'failed-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'failedActionsLabel', objectTable: TABLE },
				},
				displayStyle: 'text',
				conditionalVisibility: {
					field: 'failedActionsLabel',
					condition: 'is_not_null',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'danger' },
				},
			},
		],
		headers: [{ id: 'h1', text: 'Status' }],
		minWidth: 140,
		sortable: true,
		alignment: 'left',
		sortFields: [{ direction: 'ASC', fieldName: 'status', objectTable: TABLE }],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'spam',
		fields: [
			{
				id: 'spam-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'spamStateLabel', objectTable: TABLE },
				},
				displayStyle: 'badge',
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'status_map', statusMap: SPAM_STYLE },
				},
			},
		],
		headers: [{ id: 'h1', text: 'Spam check' }],
		minWidth: 120,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'ASC', fieldName: 'spamState', objectTable: TABLE },
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'assignee',
		fields: [text('assignee-name', 'assigneeName')],
		headers: [{ id: 'h1', text: 'Assigned to' }],
		minWidth: 140,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
];

const option = (id: string, label: string): FilterOption => ({ id, label });

function tab(
	id: string,
	name: string,
	sortOrder: number,
	filterRules: ListBuilderList['filterRules'],
): ListBuilderList {
	return {
		id,
		cacheTTL: 30000,
		name,
		type: 'default',
		active: true,
		sortOrder,
		...(sortOrder === 1
			? { columns: COLUMNS }
			: { inheritsFrom: 'form-submissions-to-review' }),
		filterRules,
		defaultSortId: 'column-received-desc',
	};
}

export const FORM_SUBMISSIONS_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		tab('form-submissions-to-review', 'To review', 1, {
			formSubmissionStatus: {
				defaultValue: [option('new', 'New'), option('in_review', 'In review')],
				locked: true,
			},
			formSubmissionSpamState: {
				defaultValue: [option('clean', 'Passed')],
				locked: true,
			},
		}),
		tab('form-submissions-failed', 'Failed actions', 2, {
			formSubmissionFailedActions: {
				defaultValue: option('Yes', 'Has a failed action'),
				locked: true,
			},
		}),
		tab('form-submissions-spam', 'Spam and unverified', 3, {
			formSubmissionSpamState: {
				defaultValue: [
					option('spam', 'Spam'),
					option('duplicate', 'Duplicate'),
					option('unverified', 'Unverified'),
				],
				locked: true,
			},
		}),
		tab('form-submissions-done', 'Done', 4, {
			formSubmissionStatus: {
				defaultValue: [
					option('converted', 'Converted'),
					option('linked', 'Linked'),
					option('dismissed', 'Dismissed'),
				],
				locked: true,
			},
		}),
		tab('form-submissions-all', 'All', 5, {}),
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'formSubmissionKeywords',
			'formSubmissionForm',
			'formSubmissionStorefront',
			'formSubmissionStatus',
			'formSubmissionSpamState',
			'formSubmissionAssignedToMe',
			'formSubmissionFailedActions',
			'formSubmissionMatched',
			'formSubmissionReceived',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/forms/submissions/:id',
			objectLabel: 'Form Submission',
			objectTable: TABLE,
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'createdDate', fieldLabel: 'Received' },
				{ fieldName: 'submitterName', fieldLabel: 'Name' },
				{ fieldName: 'submitterEmail', fieldLabel: 'Email' },
				{ fieldName: 'submitterPhone', fieldLabel: 'Phone' },
				{ fieldName: 'submitterCompany', fieldLabel: 'Company' },
				{ fieldName: 'formName', fieldLabel: 'Form' },
				{ fieldName: 'storefrontName', fieldLabel: 'Storefront' },
				{ fieldName: 'matchSummary', fieldLabel: 'Who it is' },
				{ fieldName: 'clientName', fieldLabel: 'Client' },
				{ fieldName: 'statusLabel', fieldLabel: 'Status' },
				{ fieldName: 'spamStateLabel', fieldLabel: 'Spam check' },
				{ fieldName: 'assigneeName', fieldLabel: 'Assigned to' },
				{ fieldName: 'failedActionsLabel', fieldLabel: 'Failed actions' },
			],
		},
	],
};
